import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { permissionService } from '../../services/permissionService';
import { tableService } from '../../services/tableService';
import { userService } from '../../services/userService';
import { authService } from '../../services/api';
import './Tables.css';

const TableDetails = () => {
  const { tableName } = useParams();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionForm, setPermissionForm] = useState({
    userId: '',
    canInsert: true,
    canDelete: false,
  });
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [rowForm, setRowForm] = useState({});
  const [latestOnly, setLatestOnly] = useState(false);
  const [rowLimit, setRowLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isInsertDataOpen, setIsInsertDataOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTable = async () => {
      setLoading(true);
      const result = await tableService.getTable(tableName);
      if (result.success) {
        setTable(result.table);
        setError('');
      } else {
        setError(result.error);
      }
      setLoading(false);
    };

    loadTable();
  }, [tableName]);

  useEffect(() => {
    const loadUsers = async () => {
      const result = await userService.getUsers();
      if (result.success) {
        setUsers(result.users || []);
      }
    };

    const loadPermissions = async () => {
      const result = await permissionService.listAll();
      if (result.success) {
        setPermissions(result.permissions || []);
      }
    };

    loadUsers();
    loadPermissions();
  }, [tableName]);

  useEffect(() => {
    if (table) {
      const initialForm = {};
      (table.schema_definition?.columns || []).forEach((column) => {
        initialForm[column.name] = '';
      });
      setRowForm(initialForm);
    }
  }, [table]);

  const tablePermissions = useMemo(
    () => permissions.filter((permission) => permission.table_name === tableName),
    [permissions, tableName]
  );

  const userLookup = useMemo(
    () => users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {}),
    [users]
  );

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    const result = await dataService.listRows(tableName, {
      limit: rowLimit,
      offset: currentPage * rowLimit,
      latest: latestOnly,
      sortBy: sortBy || undefined,
      sortDir,
    });

    if (result.success) {
      setRows(result.rows);
      setRowsTotal(result.totalCount || 0);
      setRowsError('');
    } else {
      setRowsError(result.error);
    }
    setRowsLoading(false);
  }, [currentPage, latestOnly, rowLimit, sortBy, sortDir, tableName]);

  useEffect(() => {
    if (table) {
      loadRows();
    }
  }, [table, loadRows]);

  const totalPages = Math.max(1, Math.ceil(rowsTotal / rowLimit));

  const handleSort = (columnName) => {
    setLatestOnly(false);
    setCurrentPage(0);
    setSortDir((prev) => (sortBy === columnName ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortBy(columnName);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 0 || nextPage >= totalPages) return;
    setCurrentPage(nextPage);
  };

  const handlePermissionSubmit = async (event) => {
    event.preventDefault();
    if (!permissionForm.userId) {
      setPermissionError('Select a user to assign permissions.');
      return;
    }

    setPermissionLoading(true);
    setPermissionError('');
    const result = await permissionService.create({
      user_id: permissionForm.userId,
      table_name: tableName,
      can_insert: permissionForm.canInsert,
      can_delete: permissionForm.canDelete,
    });

    if (result.success) {
      const refreshed = await permissionService.listAll();
      if (refreshed.success) {
        setPermissions(refreshed.permissions || []);
      }
      setPermissionForm({
        userId: '',
        canInsert: true,
        canDelete: false,
      });
    } else {
      setPermissionError(result.error);
    }
    setPermissionLoading(false);
  };

  const handlePermissionToggle = async (permissionId, field, value) => {
    const result = await permissionService.update(permissionId, {
      [field]: value,
    });

    if (result.success) {
      setPermissions((prev) =>
        prev.map((permission) =>
          permission.id === permissionId ? result.permission : permission
        )
      );
    }
  };

  const handlePermissionDelete = async (permissionId) => {
    if (window.confirm('Remove this permission?')) {
      const result = await permissionService.delete(permissionId);
      if (result.success) {
        setPermissions((prev) => prev.filter((permission) => permission.id !== permissionId));
      }
    }
  };

  const parseInputValue = (value, type) => {
    if (value === '') return undefined;
    const normalizedType = (type || '').toLowerCase().replace(/\(.*\)/, '').trim();
    if (['integer', 'bigint', 'smallint', 'decimal', 'numeric', 'real', 'double precision'].includes(normalizedType)) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }
    if (['boolean', 'bool'].includes(normalizedType)) {
      return value === 'true' || value === '1' || value === 'yes';
    }
    if (['json', 'jsonb'].includes(normalizedType)) {
      try {
        return JSON.parse(value);
      } catch (error) {
        throw new Error('Invalid JSON payload');
      }
    }
    return value;
  };

  const handleRowSubmit = async (event) => {
    event.preventDefault();
    setRowsError('');

    const payload = {};
    try {
      (table.schema_definition?.columns || []).forEach((column) => {
        const rawValue = rowForm[column.name];
        if (rawValue !== '' && rawValue !== undefined) {
          payload[column.name] = parseInputValue(rawValue, column.type);
        }
      });
    } catch (error) {
      setRowsError(error.message || 'Invalid row payload');
      return;
    }

    const result = await dataService.insertRow(tableName, payload);
    if (result.success) {
      loadRows();
      setRowForm((prev) => {
        const cleared = {};
        Object.keys(prev).forEach((key) => {
          cleared[key] = '';
        });
        return cleared;
      });
    } else {
      setRowsError(result.error);
    }
  };

  const primaryKeyColumn = useMemo(() => {
    if (!table) return null;
    return (table.schema_definition?.columns || []).find((col) => col.primary_key);
  }, [table]);

  const displayColumns = useMemo(() => {
    const schemaColumns = (table?.schema_definition?.columns || []).map((c) => c.name);
    const extraColumns = rows.length > 0
      ? Object.keys(rows[0]).filter((key) => !schemaColumns.includes(key))
      : [];
    const combinedColumns = [...schemaColumns, ...extraColumns];
    if (!combinedColumns.includes('username')) return combinedColumns;
    const columnsWithoutUsername = combinedColumns.filter((column) => column !== 'username');
    columnsWithoutUsername.splice(Math.min(1, columnsWithoutUsername.length), 0, 'username');
    return columnsWithoutUsername;
  }, [rows, table]);

  const handleRowDelete = async (row) => {
    if (!primaryKeyColumn) {
      setRowsError('No primary key available to delete rows.');
      return;
    }

    const rowId = row[primaryKeyColumn.name];
    if (rowId === undefined) {
      setRowsError('Selected row has no primary key value.');
      return;
    }

    if (window.confirm(`Delete row ${rowId}?`)) {
      const result = await dataService.deleteRow(tableName, rowId);
      if (result.success) {
        loadRows();
      } else {
        setRowsError(result.error);
      }
    }
  };

  return (
    <div className="tables-page">
      <header className="tables-header">
        <div className="header-content">
          <div>
            <h1>Table Details</h1>
            <p>{tableName}</p>
          </div>
          <div className="tables-header-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard/tables')}>
              Back to Tables
            </button>
          </div>
        </div>
      </header>

      <main className="tables-main">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading table details...</p>
          </div>
        ) : error ? (
          <div className="notification error">{error}</div>
        ) : (
          <div className="table-details-layout">
            <section className="table-details-card">
              <div className="card-header-row">
                <h2>Schema</h2>
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={isSchemaOpen}
                  aria-label={isSchemaOpen ? 'Collapse schema' : 'Expand schema'}
                  onClick={() => setIsSchemaOpen((prev) => !prev)}
                >
                  {isSchemaOpen ? '▴' : '▾'}
                </button>
              </div>
              {isSchemaOpen && (
                <>
                  <p>{table.description || 'No description provided.'}</p>
                  <div className="schema-list">
                    {(table.schema_definition?.columns || []).map((column) => (
                      <div key={column.name} className="schema-item">
                        <div>
                          <strong>{column.name}</strong>
                          <span className="schema-type">{column.type}</span>
                        </div>
                        <div className="schema-meta">
                          {column.primary_key && <span className="schema-tag">PK</span>}
                          {!column.nullable && <span className="schema-tag">Required</span>}
                          {column.unique && <span className="schema-tag">Unique</span>}
                          {column.default && <span className="schema-tag">Default</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="table-details-card">
              <div className="card-header-row">
                <h2>Permissions</h2>
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={isPermissionsOpen}
                  aria-label={isPermissionsOpen ? 'Collapse permissions' : 'Expand permissions'}
                  onClick={() => setIsPermissionsOpen((prev) => !prev)}
                >
                  {isPermissionsOpen ? '▴' : '▾'}
                </button>
              </div>
              {isPermissionsOpen && (
                <>
                  <p>Grant insert/delete access per user for this table.</p>
                  {permissionError && (
                    <div className="notification error">{permissionError}</div>
                  )}
                  <form className="permission-form" onSubmit={handlePermissionSubmit}>
                    <div className="form-group">
                      <label>User</label>
                      <select
                        value={permissionForm.userId}
                        onChange={(event) => setPermissionForm((prev) => ({
                          ...prev,
                          userId: event.target.value,
                        }))}
                      >
                        <option value="">Select user</option>
                        {users
                          .filter((user) => !authService.isAdmin(user))
                          .map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.email}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="checkbox-row">
                      <label>
                        <input
                          type="checkbox"
                          checked={permissionForm.canInsert}
                          onChange={(event) => setPermissionForm((prev) => ({
                            ...prev,
                            canInsert: event.target.checked,
                          }))}
                        />
                        Insert
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={permissionForm.canDelete}
                          onChange={(event) => setPermissionForm((prev) => ({
                            ...prev,
                            canDelete: event.target.checked,
                          }))}
                        />
                        Delete
                      </label>
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={permissionLoading}>
                      {permissionLoading ? 'Saving...' : 'Grant Permission'}
                    </button>
                  </form>

                  <div className="permissions-table">
                    {tablePermissions.length === 0 ? (
                      <div className="empty-state">
                        <p>No permissions assigned for this table.</p>
                      </div>
                    ) : (
                      tablePermissions.map((permission) => (
                        <div key={permission.id} className="permissions-row">
                          <div>
                            <strong>{userLookup[permission.user_id]?.email || permission.user_id}</strong>
                            <div className="permission-tags">
                              {permission.can_insert && <span className="schema-tag">Insert</span>}
                              {permission.can_delete && <span className="schema-tag">Delete</span>}
                              {!permission.can_insert && !permission.can_delete && (
                                <span className="schema-tag">View</span>
                              )}
                            </div>
                          </div>
                          <div className="permission-actions">
                            <label>
                              <input
                                type="checkbox"
                                checked={permission.can_insert}
                                onChange={(event) => handlePermissionToggle(permission.id, 'can_insert', event.target.checked)}
                              />
                              Insert
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={permission.can_delete}
                                onChange={(event) => handlePermissionToggle(permission.id, 'can_delete', event.target.checked)}
                              />
                              Delete
                            </label>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handlePermissionDelete(permission.id)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </section>

            <section className="table-details-card">
              <div className="card-header-row">
                <h2>Insert Data</h2>
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={isInsertDataOpen}
                  aria-label={isInsertDataOpen ? 'Collapse insert data' : 'Expand insert data'}
                  onClick={() => setIsInsertDataOpen((prev) => !prev)}
                >
                  {isInsertDataOpen ? '▴' : '▾'}
                </button>
              </div>
              {isInsertDataOpen && (
                <>
                  <p>Insert a new row into this table.</p>
                  {rowsError && <div className="notification error">{rowsError}</div>}
                  <form className="row-form" onSubmit={handleRowSubmit}>
                    {(table.schema_definition?.columns || []).map((column) => (
                      <div key={column.name} className="form-group">
                        <label>
                          {column.name}
                          {!column.nullable && !column.primary_key && <span className="required">*</span>}
                        </label>
                        <input
                          type="text"
                          value={rowForm[column.name] || ''}
                          onChange={(event) =>
                            setRowForm((prev) => ({
                              ...prev,
                              [column.name]: event.target.value,
                            }))
                          }
                          placeholder={column.type}
                        />
                      </div>
                    ))}
                    <button className="btn btn-primary" type="submit">
                      Insert Row
                    </button>
                  </form>
                </>
              )}
            </section>

            <section className="table-details-card">
              <div className="data-header">
                <div>
                  <h2>View Data</h2>
                  <p>Browse the current contents of this table.</p>
                </div>
                <div className="data-controls">
                  <label>
                    Latest only
                    <input
                      type="checkbox"
                      checked={latestOnly}
                      onChange={(event) => {
                        setLatestOnly(event.target.checked);
                        setCurrentPage(0);
                        if (event.target.checked) {
                          setSortBy('');
                        }
                      }}
                    />
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={rowLimit}
                    onChange={(event) => {
                      setRowLimit(Number(event.target.value));
                      setCurrentPage(0);
                    }}
                  />
                  <button className="btn btn-secondary" onClick={loadRows}>
                    Refresh
                  </button>
                </div>
              </div>

              {rowsError && <div className="notification error">{rowsError}</div>}

              {rowsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading rows...</p>
                </div>
              ) : (
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        {displayColumns.map((columnName) => (
                          <th key={columnName}>
                            <button
                              type="button"
                              className="sortable-header"
                              onClick={() => handleSort(columnName)}
                            >
                              {columnName}
                              {sortBy === columnName && (
                                <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </button>
                          </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          {displayColumns.map((columnName) => (
                            <td key={`${index}-${columnName}`}>
                              {row[columnName] !== undefined ? String(row[columnName]) : ''}
                            </td>
                          ))}
                          <td>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleRowDelete(row)}
                              disabled={!primaryKeyColumn}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                  {rows.length === 0 && (
                    <div className="empty-state">
                      <p>No rows found.</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default TableDetails;
